import type { AsyncData } from '#app';

type AsyncDataCompatible<T, E> = {
  error: AsyncData<T, E>['error'],
  refresh: (...args: any[]) => Promise<T | undefined>,
};

/** Wrapper around nuxt's `useAsyncData` composable. It automatically shows an error notification when the async request fails.
 * @param notificationOptions The notification text and optional retry action text. If retry is provided, clicking the action button will re-trigger the async request.
 * @param asyncData The AsyncData object returned by `useAsyncData`
 * @returns The same AsyncData object
 */
export function useErrorNotification<D extends AsyncDataCompatible<T, E>, T, E>(
  notificationOptions: { message: NotificationItemStored['message'] | ((error: unknown) => string), retry?: string },
  asyncData: D,
): D {
  const notificationStore = useNotificationsStore();
  const { handle: handleBookingEngineError } = useBookingEngineError();

  watch(asyncData.error, (error) => {
    if (!error) return;

    console.error(error);
    useO11y().reportError(error);
    if (handleBookingEngineError(error, { retry: asyncData.refresh })) return;

    const message = typeof notificationOptions.message === 'function'
      ? notificationOptions.message(error)
      : notificationOptions.message;

    const errorNotificationId = notificationStore.add({
      status: 'danger',
      message,
      timeout: 5000,
      actionPrimary: notificationOptions.retry,
      onPrimaryClick: notificationOptions.retry ? (() => {
        notificationStore.remove(errorNotificationId);
        asyncData.refresh();
      }) : undefined,
    });
  });

  return asyncData;
}
